import { StripeBillingInterval, StripeProductType } from '../enums/stripe.enum';
import {
  SubscriptionPlanSummary,
  SubscriptionSummary,
} from '../interfaces/subscription';

/**
 * Maps a Stripe plan interval string to the booking enum.
 * @param {string | null | undefined} interval Raw plan interval
 * @returns {StripeBillingInterval | null} Month/year, or null when unknown
 */
export function toStripeBillingInterval(
  interval: string | null | undefined,
): StripeBillingInterval | null {
  if (interval === StripeBillingInterval.YEAR) {
    return StripeBillingInterval.YEAR;
  }
  if (interval === StripeBillingInterval.MONTH) {
    return StripeBillingInterval.MONTH;
  }
  return null;
}

/**
 * Whether a package plan is a paid Stripe item that locks billing interval.
 * @param {SubscriptionPlanSummary | null | undefined} plan Plan summary
 * @returns {boolean}
 */
function isPaidPackagePlan(
  plan: SubscriptionPlanSummary | null | undefined,
): boolean {
  if (!plan?.interval) {
    return false;
  }
  return (plan.cost ?? 0) > 0;
}

/**
 * Paid billing interval of a single package plan.
 * @param {SubscriptionPlanSummary | null | undefined} plan Plan summary
 * @returns {StripeBillingInterval | null}
 */
function intervalOfPaidPlan(
  plan: SubscriptionPlanSummary | null | undefined,
): StripeBillingInterval | null {
  if (!isPaidPackagePlan(plan)) {
    return null;
  }
  return toStripeBillingInterval(plan?.interval);
}

/**
 * Interval locked by other paid package items.
 * Single-product booking modals cannot mix intervals with existing items.
 * Yearly wins if mixed (invalid) packages are present.
 * @param {SubscriptionSummary | null | undefined} summary Live subscription summary
 * @param {StripeProductType | null} excludeType Product being edited; null considers all paid items
 * @returns {StripeBillingInterval | null} Sibling interval, or null when none
 */
export function siblingPackageInterval(
  summary: SubscriptionSummary | null | undefined,
  excludeType: StripeProductType | null,
): StripeBillingInterval | null {
  if (!summary) {
    return null;
  }
  const intervals: StripeBillingInterval[] = [];
  if (excludeType !== StripeProductType.BASIC) {
    const interval = intervalOfPaidPlan(summary.baseSubscription);
    if (interval) {
      intervals.push(interval);
    }
  }
  if (excludeType !== StripeProductType.AGENTS) {
    const interval = intervalOfPaidPlan(summary.agentsSubscription);
    if (interval) {
      intervals.push(interval);
    }
  }
  if (excludeType !== StripeProductType.ADD_ON) {
    const interval = intervalOfPaidPlan(summary.atsSubscription);
    if (interval) {
      intervals.push(interval);
    }
  }
  if (intervals.includes(StripeBillingInterval.YEAR)) {
    return StripeBillingInterval.YEAR;
  }
  if (intervals.includes(StripeBillingInterval.MONTH)) {
    return StripeBillingInterval.MONTH;
  }
  return null;
}

/**
 * Interval to open a single-product booking modal on.
 * Sibling package items win; otherwise the product's own interval; otherwise fallback.
 * @param {{ ownInterval: StripeBillingInterval | null; siblingInterval: StripeBillingInterval | null; fallback: StripeBillingInterval }} params Interval inputs
 * @returns {StripeBillingInterval}
 */
export function resolveBookingInterval(params: {
  ownInterval: StripeBillingInterval | null;
  siblingInterval: StripeBillingInterval | null;
  fallback: StripeBillingInterval;
}): StripeBillingInterval {
  if (params.siblingInterval) {
    return params.siblingInterval;
  }
  return params.ownInterval ?? params.fallback;
}

/**
 * Drops interval tabs that cannot be booked while another package item is locked.
 * @template T
 * @param {ReadonlyArray<T>} options Full month/year tabs
 * @param {StripeBillingInterval | null} lockedInterval Interval required by a sibling item
 * @returns {ReadonlyArray<T>}
 */
export function filterBillingIntervalOptions<
  T extends { value: StripeBillingInterval },
>(
  options: ReadonlyArray<T>,
  lockedInterval: StripeBillingInterval | null,
): ReadonlyArray<T> {
  if (!lockedInterval) {
    return options;
  }
  return options.filter((option) => option.value === lockedInterval);
}
